import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Users, Check, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (f, today) => {
  let time = 'Any time';
  if (f.followup_time) {
    const [h, m] = f.followup_time.split(':').map(Number);
    time = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  if (f.followup_date === today) return `Today · ${time}`;
  const d = new Date(`${f.followup_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${d} · ${time}`;
};

const FollowUpRow = ({ f, today, onOpen, onDone }) => (
  <motion.li
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className={`flex items-center gap-3 p-2.5 rounded-lg ${f.overdue ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-white/60 dark:bg-slate-800/60'}`}
    data-testid={`today-followup-${f.contact_id}`}
  >
    <button className="flex-1 min-w-0 text-left" onClick={() => onOpen(f.contact_id)} data-testid={`today-followup-open-${f.contact_id}`}>
      <p className="font-medium text-sm truncate hover:underline">{f.name}</p>
      <p className={`text-xs flex items-center gap-1 ${f.overdue ? 'text-red-600' : 'text-muted-foreground'}`}>
        {f.overdue ? <AlertCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        {f.overdue ? 'Overdue · ' : ''}{fmtWhen(f, today)}
      </p>
    </button>
    <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => onDone(f.contact_id)} data-testid={`today-followup-done-${f.contact_id}`}>
      <Check className="w-4 h-4 mr-1" /> Done
    </Button>
  </motion.li>
);

export const TodayFollowUpsCard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ today: '', followups: [], overdue: 0 });

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/contacts/followups/today`, getAuthHeaders()).catch(() => null);
    if (res) setData(res.data);
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => {
    try {
      await axios.post(`${API}/contacts/${id}/complete-followup`, {}, getAuthHeaders());
      setData((d) => ({ ...d, followups: d.followups.filter((f) => f.contact_id !== id) }));
      toast.success('Follow-up marked done');
    } catch (e) {
      toast.error('Failed to mark follow-up done');
    }
  };

  const list = data.followups || [];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
      <Card className="border-pink-200 dark:border-pink-800" data-testid="today-followups-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-pink-700 dark:text-pink-300 text-base">
            <Users className="w-5 h-5" /> Today's Follow-Ups
            <span className="ml-auto text-xs font-normal text-muted-foreground" data-testid="today-followups-count">
              {list.length} to contact
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3" data-testid="today-followups-empty">
              No follow-ups due today. You're all caught up!
            </p>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto">
              {list.map((f) => (
                <FollowUpRow key={f.contact_id} f={f} today={data.today} onOpen={(id) => navigate(`/contacts?open=${id}`)} onDone={markDone} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TodayFollowUpsCard;
